import { isEmpty } from 'lodash/fp';
import styled, { css } from 'styled-components';
import { getToken, IconButton, Stack, Tabs, Tag } from '@optimus-web/core';
import BreadcrumbList, { Breadcrumbs } from '../../../../components/BreadcrumbList';
import { ClassificationIcons, PrintHiddenDiv } from '../../../../components/commander-ui';
import { COLOR_WHITE, Z_INDEX_MODAL_HEADER } from '../../../../components/commander-ui/constants';
import { TabsConfig } from '../../../../modules/modalManager';
import { HeadingAction } from '../HeadingAction/Action/types';
import ActionHeader from '../HeadingAction/ActionHeader';
import TwoLineTitle from './TwoLineTitle';

export interface ModalHeaderProps<T extends string = string> {
    primaryAction?: HeadingAction | null;
    actions?: HeadingAction[];
    title?: React.ReactNode;
    imageUrl?: string | null;
    classifications?: number | null;
    description?: React.ReactNode;
    breadcrumbs?: Breadcrumbs;
    tabsConfig?: TabsConfig<T>;
    children?: React.ReactNode;
    shadowed?: boolean;
    tags?: $TSFixMe[];
}

const ModalHeader: React.FC<ModalHeaderProps> = ({
    description,
    breadcrumbs,
    title,
    primaryAction,
    classifications,
    children,
    imageUrl,
    tabsConfig,
    actions,
    shadowed = true,
    tags,
}) => {
    const { activeTabValue = '', handleTabClick, inline = false, tabs = [] } = tabsConfig || {};

    const showTabs = (config?: TabsConfig) =>
        typeof config !== 'undefined' && !isEmpty(config) && config.tabs.length > 1;

    const content =
        children ||
        (breadcrumbs != null && breadcrumbs.length > 0 ? (
            <BreadcrumbList breadcrumbs={breadcrumbs} />
        ) : (
            <Stack inline spacing="200" itemAlignment="center">
                <TwoLineTitle description={description} imageUrl={imageUrl != null ? imageUrl : undefined}>
                    {title}
                    <ClassificationIcons classifications={classifications} />
                </TwoLineTitle>
                {tags?.length ? tags.map((tag) => <Tag variant={tag.variant}>{tag.title}</Tag>) : null}
            </Stack>
        ));

    return (
        <StyledHeader shadowed={shadowed} data-test-modal-header>
            <ActionHeader actions={actions}>
                {/* @ts-expect-error $TSFixMeError: No overload matches this call. */}
                {primaryAction && <StyledIconButton variant="ghost" {...primaryAction} />}
                {content}
            </ActionHeader>
            {showTabs(tabsConfig) ? (
                <PrintHiddenDiv>
                    <Tabs activeTabValue={activeTabValue} inline={inline} onTabClick={handleTabClick} underline={false}>
                        {tabs}
                    </Tabs>
                </PrintHiddenDiv>
            ) : null}
        </StyledHeader>
    );
};

const StyledIconButton = styled(IconButton)`
    margin-left: -8px;
    margin-right: 8px;
`;

const StyledHeader = styled.div<{ shadowed?: boolean }>`
    z-index: ${Z_INDEX_MODAL_HEADER};
    background: ${COLOR_WHITE};
    position: relative;
    ${(props) =>
        props.shadowed &&
        css`
            border-bottom: ${getToken('borderWidth150')}px solid ${getToken('borderDisabled')};
        `}
`;

export default ModalHeader;
